import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveBrandGamificationItems } from '@/store/slices/casino-instance';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';

import { gaugeStatusLabel, type GaugeStatusLabel } from '../theme/tokens';
import {
  calibratedRemainingScale,
  calibrateTotals,
  coverageTotals,
  type FeatureScores,
  FULL_COVERAGE,
  isCalibrating,
  overallScore,
  unscoredActiveTiers,
} from '../utils/catalog';

export type ProductScore = {
  /** Per-axis COVERAGE (0-100): market / profitability / stickiness, each the brand's credited
   *  weights over that axis's live budget (every active catalog item at its top scoring tier),
   *  after the new-website launch calibration (see {@link coverageTotals}, {@link calibrateTotals}). */
  totals: FeatureScores;
  /** Overall Product Score (weighted blend of the coverages, rounded up). */
  score: number;
  /** Band label for `score` (Poor / Sustainable / Optimal), shared with the gauge colour band. */
  status: GaugeStatusLabel;
  /** Per-axis multiplier the Product page applies to each feature card's "to unlock" deltas, so
   *  the calibrated score and the remaining deltas keep summing to the full budget (100 per
   *  axis). 1 per axis once calibration ends (see {@link calibratedRemainingScale}). */
  deltaScale: FeatureScores;
  /** True while the launch calibration is still lifting the score (website younger than the
   *  calibration window). Drives the Product page's "Calibrating" note. */
  calibrating: boolean;
  /** True until the catalog resolves the first time (drives the insights skeleton). */
  loading: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Fractional days since an ISO launch date; `Infinity` when absent/unparseable so the
 *  calibration is treated as fully elapsed (no lift) for an unknown launch date. */
const daysSinceLaunch = (createdAt: string | undefined): number => {
  if (!createdAt) {
    return Infinity;
  }
  const launched = Date.parse(createdAt);
  return Number.isNaN(launched) ? Infinity : Math.max(0, (Date.now() - launched) / DAY_MS);
};

/**
 * The brand's Product Score, computed from the `/configurator` catalog joined with the active
 * brand's enabled features: per axis, the credited `score_*` weights of the owned tiers over the
 * live budget (every ACTIVE catalog item at its top SCORING tier), normalized to 0-100, then
 * blended. Retired (`is_active: false`) and unscored tiers and platform-disabled items sit outside
 * the scoring set (see `utils/catalog`). SINGLE SOURCE OF TRUTH shared by the Product page's
 * insights gauges and the Home dashboard's Product gauge, so the two can never disagree (mirrors
 * how `acquisitionScoreForVariant` backs the Acquisition gauge). Brand-scoped through
 * `selectActiveBrandGamificationItems`, which reads the lagged `displayBrand` like the rest of the
 * page content, so it tracks a brand switch.
 *
 * Owns the catalog fetch (dispatched once when it is neither loaded nor in flight), so any
 * consumer triggers the load. Before the catalog resolves the totals are all zero (score 0 / Poor).
 */
export const useProductScore = (): ProductScore => {
  const dispatch = useAppDispatch();
  const enabledItems = useAppSelector(selectActiveBrandGamificationItems);
  const catalog = useAppSelector((state) => state.configuratorConfig.gamificationItems);
  const loaded = useAppSelector((state) => state.configuratorConfig.loaded);
  const loading = useAppSelector((state) => state.configuratorConfig.loading);
  // The website's launch date drives the calibration lift; read from `displayBrand`
  // (like the items) so it lags through a brand switch with the rest of the content.
  const createdAt = useAppSelector((state) => state.casinoInstance.displayBrand?.created_at);

  // Dispatch the catalog fetch at most once per mount. Guarding only on
  // `!loaded && !loading` re-fires on every failure — a rejected fetch leaves
  // BOTH false, so the effect re-runs and hammers /configurator in a tight loop.
  // The ref makes it one-shot; a fresh mount (re-navigation) retries.
  const requested = React.useRef(false);
  React.useEffect(() => {
    if (!loaded && !loading && !requested.current) {
      requested.current = true;
      dispatch(fetchConfiguratorConfig());
    }
  }, [dispatch, loaded, loading]);

  // Recomputed once per minute so the calibration lift visibly decays as the website
  // ages (and the "Calibrating…" note clears on its own when the window elapses),
  // without the score jumping on every render. Re-syncs on a brand switch (createdAt
  // change); the interval is only armed while still calibrating and self-clears the
  // moment the window elapses, so a settled website carries no live timer.
  const [ageDays, setAgeDays] = React.useState(() => daysSinceLaunch(createdAt));
  React.useEffect(() => {
    const current = daysSinceLaunch(createdAt);
    setAgeDays(current);
    if (!isCalibrating(current)) {
      return;
    }
    const id = window.setInterval(() => {
      const next = daysSinceLaunch(createdAt);
      setAgeDays(next);
      if (!isCalibrating(next)) {
        window.clearInterval(id);
      }
    }, 60 * 1000);
    return () => window.clearInterval(id);
  }, [createdAt]);

  // Data-quality guard, dev only. Syncs with the console: an active, purchasable tier authored
  // without usable score_* values silently drops out of the scoring set (the rung below becomes
  // that item's top), so the gauges look plausible while the budget is wrong. Surface it once per
  // catalog load so the authoring gap is fixed at the source (the CMS), not masked here.
  React.useEffect(() => {
    if (!import.meta.env.DEV || !loaded) {
      return;
    }
    const unscored = unscoredActiveTiers(catalog);
    if (unscored.length > 0) {
      console.warn(
        '[useProductScore] active tiers without usable score_* values, excluded from the scoring set:',
        unscored.map(({ item, tier }) => `${item.name} / ${tier.name} (tier ${tier.id})`),
      );
    }
  }, [catalog, loaded]);

  const score = React.useMemo<Omit<ProductScore, 'loading'>>(() => {
    // Normalized per-axis coverage (0-100), then the launch calibration, then the overall score
    // from the calibrated coverages so the headline gauge and the three sub-gauges always agree.
    const baseTotals = coverageTotals(enabledItems, catalog);
    const totals = calibrateTotals(baseTotals, ageDays);
    const overall = overallScore(totals);
    // The matching multiplier for the feature cards' remaining deltas, so the calibrated score
    // and the cards keep summing to the full budget (100 per axis) across the calibration window.
    const deltaScale = calibratedRemainingScale(baseTotals, FULL_COVERAGE, ageDays);
    return { totals, score: overall, status: gaugeStatusLabel(overall), deltaScale, calibrating: isCalibrating(ageDays) };
  }, [enabledItems, catalog, ageDays]);

  return { ...score, loading: !loaded };
};

export default useProductScore;
